import { Fragment } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Pencil, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type {
  GetListingTransactionsDataQuery,
  UserCardDisplayNameFragment,
} from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import {
  listingCardBodyTextSx,
  listingCardSecondaryTextSx,
} from '@modules/_shared/components/listing-overview/listing-overview-styles';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { type Currency } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

interface ListingTransactionsCardProps {
  listingId: string | null | undefined;
  openedDefault?: boolean;
  onEditTransaction?: (transactionId: string) => void;
  readOnly?: boolean;
}

export const GET_LISTING_TRANSACTIONS_DATA = gql(/* GraphQL */ `
  query GetListingTransactionsData($id: uuid!) {
    property_transactions(
      where: { lot_id: { _eq: $id } }
      order_by: [{ payment_date: asc_nulls_last }, { created_at: asc }]
    ) {
      ...TransactionFormFragment
      commissions(order_by: { payment_date: asc }) {
        id
        type
        amount
        contact {
          ...UserCardDisplayName
        }
      }
      lot {
        currency
      }
      property {
        country_code
      }
    }
  }
`);

type PropertyTransaction =
  GetListingTransactionsDataQuery['property_transactions'][number];

type CommissionData = {
  id: any;
  amount?: number | null;
  sharePercentage: number;
  contact?: UserCardDisplayNameFragment | null;
};

const computeCommission = (transaction: PropertyTransaction) => {
  const totalCommission = transaction.commissions.find(
    ({ type }) => type === 'total',
  )?.amount;
  const sharePercentage =
    ((totalCommission ?? 0) / (transaction.purchase_price ?? 0)) * 100;

  const commissionSplits = transaction.commissions.reduce((acc, commision) => {
    if (commision.type === 'split') {
      acc.push({
        ...commision,
        sharePercentage:
          ((commision.amount ?? 0) / (transaction.purchase_price ?? 0)) * 100,
      });
    }

    return acc;
  }, [] as CommissionData[]);

  return {
    totalCommission,
    sharePercentage,
    commissionSplits,
  };
};

const Commission: React.FC<{
  transaction: PropertyTransaction;
  currency: Currency;
}> = ({ transaction, currency }) => {
  const { t, locale } = useLocale();
  const signatureDate =
    transaction.signature_date != null
      ? t('Signed {{date}}', {
          date: toHumanReadableDate(locale, transaction.signature_date, {
            month: 'numeric',
          }),
        })
      : null;
  const estimatedSignatureDate =
    transaction.estimated_signature_date != null
      ? t('Predicted signature date: {{date}}', {
          date: toHumanReadableDate(
            locale,
            transaction.estimated_signature_date,
            { month: 'numeric' },
          ),
        })
      : null;

  const { totalCommission, sharePercentage, commissionSplits } =
    computeCommission(transaction);

  return (
    <Stack spacing={0.25}>
      <Typography
        sx={theme => ({
          ...theme.mixins.truncate(),
          ...listingCardSecondaryTextSx,
        })}
      >
        {[
          signatureDate ?? estimatedSignatureDate ?? t('Not signed yet'),
          ...(totalCommission != null
            ? [
                t('{{percentage}}% commission', {
                  percentage: sharePercentage.toLocaleString(locale, {
                    maximumFractionDigits: 1,
                  }),
                }),
                formatPrice(totalCommission, locale, currency),
              ]
            : []),
        ].join(' • ')}
      </Typography>
      {commissionSplits.map(commission => (
        <Typography
          key={commission.id}
          sx={theme => ({
            ...theme.mixins.truncate(),
            ...listingCardSecondaryTextSx,
          })}
        >
          {[
            commission.contact != null
              ? getUserDisplayName(commission.contact)
              : null,
            commission.amount != null
              ? formatPrice(commission.amount, locale, currency)
              : '-',
          ]
            .filter(Boolean)
            .join(' • ')}
        </Typography>
      ))}
    </Stack>
  );
};

const TransactionSkeletonContent: React.FC = () => (
  <Stack spacing={0.5}>
    <Typography sx={listingCardBodyTextSx}>
      <Skeleton width={70} />
    </Typography>
    <Stack spacing={0.25}>
      <Typography sx={listingCardSecondaryTextSx}>
        <Skeleton width={130} />
      </Typography>
      <Typography sx={listingCardSecondaryTextSx}>
        <Skeleton width={100} />
      </Typography>
      <Typography sx={listingCardSecondaryTextSx}>
        <Skeleton width={90} />
      </Typography>
    </Stack>
  </Stack>
);

export const ListingTransactionsCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault }) => {
  const { t } = useLocale();
  return (
    <ListingAccordion title={t('Transactions')} defaultExpanded={openedDefault}>
      <TransactionSkeletonContent />
    </ListingAccordion>
  );
};

export const ListingTransactionsCard: React.FC<
  ListingTransactionsCardProps
> = ({
  listingId,
  onEditTransaction,
  openedDefault = false,
  readOnly = false,
}) => {
  const { t, locale } = useLocale();
  const { data, loading } = useQuery(GET_LISTING_TRANSACTIONS_DATA, {
    variables: { id: listingId ?? '' },
    skip: listingId == null,
  });

  return (
    <ListingAccordion
      title={t('Transactions')}
      defaultExpanded={openedDefault}
      headerAction={
        !readOnly && listingId != null ? (
          <CardHeaderIconActions
            actions={[
              {
                label: t('Add a transaction'),
                icon: <Plus size={16} />,
                link: {
                  to: `/listings/${listingId}/transaction/new/listing`,
                  search: `?selected-listing=${listingId}`,
                },
              },
            ]}
          />
        ) : undefined
      }
    >
      {!loading && data?.property_transactions.length === 0 && (
        <Typography sx={{ ...listingCardBodyTextSx, color: 'text.secondary' }}>
          {t('No transactions')}
        </Typography>
      )}

      {loading && <TransactionSkeletonContent />}

      {!loading &&
        data?.property_transactions.map((transaction, index) => {
          const currencyToFormat = (transaction.lot?.currency ??
            (transaction.property.country_code === 'CH'
              ? 'CHF'
              : 'EUR')) as Currency;

          return (
            <Fragment key={transaction.id}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Link
                to={`/listings/${listingId}/transaction/${transaction.id}`}
                css={{
                  display: 'block',
                  position: 'relative',
                  paddingRight: 32,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography
                    sx={theme => ({
                      ...theme.mixins.truncate(),
                      ...listingCardBodyTextSx,
                    })}
                  >
                    {formatPrice(
                      transaction.purchase_price,
                      locale,
                      currencyToFormat,
                    )}
                  </Typography>
                  <Commission
                    transaction={transaction}
                    currency={currencyToFormat}
                  />
                </Stack>
                {onEditTransaction != null && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: -8,
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      onEditTransaction(transaction.id);
                    }}
                  >
                    <Pencil size={16} />
                  </IconButton>
                )}
              </Link>
            </Fragment>
          );
        })}
    </ListingAccordion>
  );
};
